<template>
  <f7-page :page-content="true" name="festivos-boletin" @page:beforein="onEntrar" @page:beforeout="onSalir">
    <f7-navbar :back-link="$t('atras')">
      <f7-nav-title sliding>{{ $t('festivosboletin.titulo') }}</f7-nav-title>
      <f7-nav-right>
        <f7-button
          v-if="!error403"
          fill
          icon-f7="doc_text_search"
          :text="$t('festivosboletin.leerBoletines')"
          class="display-flex"
          @click="abrirDialogo"
        ></f7-button>
      </f7-nav-right>
    </f7-navbar>

    <f7-block v-if="error403" strong>
      <p>{{ $t('festivosboletin.noAutorizado') }}</p>
    </f7-block>

    <template v-else>
      <f7-block strong class="no-margin-bottom">
        <p class="no-margin">{{ $t('festivosboletin.explicacion') }}</p>
      </f7-block>

      <!-- Mismo criterio que la tabla de Ajustes · Festivos (#262): «no hay ninguna persona con
           municipio» solo se puede decir cuando la respuesta ya ha llegado. Mientras está en
           vuelo se dice que está cargando, y si falló se dice que falló.
           El fallo se mira ANTES que la lista. Detrás era un `v-else-if` del
           `v-if="municipios.length"`, o sea inalcanzable en cuanto hubiera municipios cargados:
           al volver a entrar en esta ruta keepAlive con la recarga fallando, en pantalla se
           quedaban los de la vez anterior sin error y sin reintento, como si hubiera ido bien
           (revisión CODEX del PR #1759). `cargarMunicipios` vacía además la lista al fallar; el
           orden es lo que impide que el error vuelva a quedar tapado. -->
      <f7-block v-if="estadoMunicipios === 'error'" strong>
        <p class="no-margin">
          {{ $t('festivosboletin.municipiosSinCargar') }}
          <f7-link class="margin-left-half" @click="cargarMunicipios()">
            {{ $t('festivosboletin.reintentar') }}
          </f7-link>
        </p>
      </f7-block>
      <f7-card v-else-if="municipios.length" class="padding-vertical-half padding-right">
        <f7-card-header>{{ $t('festivosboletin.municipiosEnUso') }}</f7-card-header>
        <f7-card-content>
          <f7-list no-hairlines class="no-margin-vertical">
            <f7-list-item
              v-for="m in municipios"
              :key="m.codigoIne"
              :title="m.nombre + ' (' + m.codigoIne + ')'"
              :after="$tc('festivosboletin.personas', m.personas, { n: m.personas })"
            ></f7-list-item>
          </f7-list>
        </f7-card-content>
      </f7-card>
      <!-- Y el «cargando» es para cuando no hay nada que enseñar todavía: el refresco de al
           volver a entrar sigue viendo la lista ya comprobada en vez de parpadear, igual que la
           tabla de Ajustes · Festivos mientras se recarga. -->
      <f7-block v-else-if="estadoMunicipios === 'cargando'" strong>
        <f7-preloader class="margin-right-half"></f7-preloader>
        <span>{{ $t('festivosboletin.cargandoMunicipios') }}</span>
      </f7-block>
      <f7-block v-else strong>
        <p>{{ $t('festivosboletin.sinMunicipios') }}</p>
      </f7-block>

      <!-- Resultado de la última lectura -->
      <template v-if="resultado">
        <f7-block-title>{{ $t('festivosboletin.resultadoLectura') }}</f7-block-title>
        <f7-block strong class="cabecera-lectura">
          <span class="dato">{{ $t('festivosboletin.fuente') }}: <b>{{ resultado.fuenteRef }}</b></span>
          <span class="dato">{{ $t('festivosboletin.documento') }}: <b>{{ resultado.nombreFichero }}</b></span>
          <span class="dato">{{ $t('festivosboletin.modelo') }}: <b>{{ resultado.modelo }}</b></span>
        </f7-block>

        <f7-block v-if="resultado.error" strong class="aviso-error">
          <p class="no-margin">{{ resultado.error }}</p>
        </f7-block>

        <!-- La IA ha leído el boletín pero deja algo fuera: se dice, o la lista parece completa. -->
        <f7-block v-if="resultado.aviso" strong class="aviso-lectura">
          <p class="no-margin">{{ $t('festivosboletin.avisoLectura') }}: {{ resultado.aviso }}</p>
        </f7-block>

        <!-- Las discrepancias van primero: son lo único que exige una decisión de RRHH. -->
        <f7-card v-if="tiene(resultado.discrepancias)" class="padding-vertical-half padding-right seccion-discrepancias">
          <f7-card-header>{{ $t('festivosboletin.discrepancias') }}</f7-card-header>
          <f7-card-content>
            <p class="padding-horizontal texto-ayuda">{{ $t('festivosboletin.discrepanciasAyuda') }}</p>
            <f7-list no-hairlines class="no-margin-vertical">
              <f7-list-item
                v-for="(d, i) in resultado.discrepancias"
                :key="'disc-' + i"
                :title="tituloDiscrepancia(d)"
                :subtitle="$t('festivosboletin.validadoDice', { fecha: fecha(d.fechaValidada), nombre: d.nombreValidado, fuente: d.fuenteRefValidada || '—' })"
                :text="$t('festivosboletin.boletinDice', { fecha: fecha(d.fechaBoletin), nombre: d.nombreBoletin, fuente: d.fuenteRefBoletin || '—' })"
              >
                <f7-icon slot="media" f7="exclamationmark_triangle_fill" size="18" color="orange"></f7-icon>
              </f7-list-item>
            </f7-list>
          </f7-card-content>
        </f7-card>

        <f7-card v-if="tiene(resultado.propuestos)" class="padding-vertical-half padding-right">
          <f7-card-header>{{ $t('festivosboletin.propuestos') }} ({{ resultado.propuestos.length }})</f7-card-header>
          <f7-card-content>
            <f7-list no-hairlines class="no-margin-vertical">
              <f7-list-item
                v-for="f in resultado.propuestos"
                :key="f.id"
                :title="fecha(f.fecha) + ' · ' + f.nombre"
                :after="$t('festivosboletin.estadoPropuesto')"
                :subtitle="etiquetaAmbito(f)"
                :text="textoBoletin(f)"
              ></f7-list-item>
            </f7-list>
          </f7-card-content>
        </f7-card>

        <f7-card v-if="tiene(resultado.yaExistentes)" class="padding-vertical-half padding-right">
          <f7-card-header>{{ $t('festivosboletin.yaExistentes') }} ({{ resultado.yaExistentes.length }})</f7-card-header>
          <f7-card-content>
            <f7-list no-hairlines class="no-margin-vertical">
              <f7-list-item
                v-for="f in resultado.yaExistentes"
                :key="'ya-' + f.id"
                :title="fecha(f.fecha) + ' · ' + f.nombre"
                :subtitle="etiquetaAmbito(f)"
                :text="textoBoletin(f)"
              ></f7-list-item>
            </f7-list>
          </f7-card-content>
        </f7-card>

        <f7-card v-if="tiene(resultado.municipiosSinLocales)" class="padding-vertical-half padding-right">
          <f7-card-header>{{ $t('festivosboletin.faltanLocales') }}</f7-card-header>
          <f7-card-content>
            <f7-list no-hairlines class="no-margin-vertical">
              <f7-list-item
                v-for="m in resultado.municipiosSinLocales"
                :key="'sinloc-' + m.codigoIne"
                :title="m.nombre + ' (' + m.codigoIne + ')'"
              ></f7-list-item>
            </f7-list>
          </f7-card-content>
        </f7-card>

        <f7-card v-if="tiene(resultado.noReconocidos)" class="padding-vertical-half padding-right seccion-descartes">
          <f7-card-header>{{ $t('festivosboletin.noReconocidos') }} ({{ resultado.noReconocidos.length }})</f7-card-header>
          <f7-card-content>
            <f7-list no-hairlines class="no-margin-vertical">
              <f7-list-item
                v-for="(n, i) in resultado.noReconocidos"
                :key="'nr-' + i"
                :title="n.motivo"
                :text="crudo(n.propuesta)"
              ></f7-list-item>
            </f7-list>
          </f7-card-content>
        </f7-card>
      </template>

      <!-- Historial. Siempre visible, aunque esté vacío: es donde hay que mirar cuando una
           lectura se corta por tiempo, y sin el botón de actualizar no había forma de volver a
           consultarlo sin salir y entrar en la pantalla. -->
      <f7-block-title>{{ $t('festivosboletin.historial') }}</f7-block-title>
      <f7-list v-if="lecturas.length" no-hairlines class="no-margin-vertical">
        <f7-list-item
          v-for="l in lecturas"
          :key="l.idLectura"
          link="#"
          :title="(l.fuenteRef || '—') + ' · ' + (l.nombreFichero || '')"
          :after="l.usuario"
          :subtitle="fechaHora(l.fecha)"
          @click.prevent="verLectura(l.idLectura)"
        ></f7-list-item>
      </f7-list>
      <!-- Y lo mismo con el historial: «todavía no se ha leído ningún boletín» es una afirmación,
           y hasta que el GET contesta no se puede hacer. Aquí el reintento ya existe —es el botón
           «Actualizar historial» de debajo—, así que el mensaje de fallo no lleva enlace. -->
      <f7-block v-else-if="estadoLecturas === 'cargando'" strong class="no-margin-vertical">
        <p class="no-margin texto-ayuda">
          <f7-preloader class="margin-right-half"></f7-preloader>
          {{ $t('festivosboletin.cargandoLecturas') }}
        </p>
      </f7-block>
      <f7-block v-else-if="estadoLecturas === 'error'" strong class="no-margin-vertical">
        <p class="no-margin texto-ayuda">{{ $t('festivosboletin.lecturasSinCargar') }}</p>
      </f7-block>
      <f7-block v-else strong class="no-margin-vertical">
        <p class="no-margin texto-ayuda">{{ $t('festivosboletin.sinLecturas') }}</p>
      </f7-block>
      <f7-block>
        <f7-button outline :disabled="refrescando" @click="cargarLecturas()">
          {{ refrescando ? $t('festivosboletin.actualizando') : $t('festivosboletin.actualizarHistorial') }}
        </f7-button>
      </f7-block>
    </template>

    <!-- Subir el boletín. No hay campo para "leer por enlace": el servidor no descarga nada. -->
    <f7-popup :opened="dialogoAbierto" @popup:closed="dialogoAbierto = false">
      <f7-page>
        <f7-navbar :title="$t('festivosboletin.leerBoletines')">
          <f7-nav-right>
            <f7-link popup-close>{{ $t('cerrar') }}</f7-link>
          </f7-nav-right>
        </f7-navbar>
        <f7-block strong class="no-margin-bottom">
          <p class="no-margin texto-ayuda">{{ $t('festivosboletin.dialogoAyuda') }}</p>
        </f7-block>
        <f7-list no-hairlines class="list-form">
          <f7-list-input
            type="number"
            :label="$t('festivosboletin.anio')"
            :value="anio"
            floating-label
            outline
            @input="anio = $event.target.value"
          ></f7-list-input>
          <f7-list-input
            type="text"
            :label="$t('festivosboletin.fuenteRef')"
            :placeholder="$t('festivosboletin.fuenteRefEjemplo')"
            :value="fuenteRef"
            floating-label
            outline
            @input="fuenteRef = $event.target.value"
          ></f7-list-input>
          <f7-list-input
            type="text"
            :label="$t('festivosboletin.fuenteUrl')"
            :placeholder="$t('festivosboletin.fuenteUrlEjemplo')"
            :value="fuenteUrl"
            floating-label
            outline
            @input="fuenteUrl = $event.target.value"
          ></f7-list-input>
          <f7-list-item :title="$t('festivosboletin.pdf')" :after="nombreFichero || $t('festivosboletin.sinFichero')"></f7-list-item>
        </f7-list>
        <input ref="inputPdf" type="file" style="display:none;" accept=".pdf" @change="onFicheroSeleccionado" />
        <f7-block>
          <f7-button outline @click="$refs.inputPdf.click()">{{ $t('festivosboletin.elegirPdf') }}</f7-button>
        </f7-block>
        <f7-block>
          <f7-button fill :disabled="!puedeLeer" @click="leer">{{ $t('festivosboletin.leer') }}</f7-button>
          <!-- Qué le falta para poder pulsarlo, junto al botón: los tres campos están arriba, en
               este mismo diálogo, pero ninguno decía que hiciera falta. -->
          <p v-if="motivoNoPuedeLeer" class="texto-ayuda no-margin-bottom">{{ motivoNoPuedeLeer }}</p>
        </f7-block>
      </f7-page>
    </f7-popup>
  </f7-page>
</template>
<style scoped>
.cabecera-lectura .dato { display: inline-block; margin-right: 18px; }
.texto-ayuda { color: #555; font-size: 13px; }
.seccion-discrepancias .card-header { color: #ff9500; font-weight: bold; }
.seccion-descartes .card-header { color: #8e8e93; font-weight: bold; }
.aviso-error { color: #d32f2f; }
.aviso-lectura { color: #ff9500; }
</style>
<script>
import RhFestivo from './../../js/db/RhFestivo.js';
import Utils from './../../js/Utils.js';
import carrilPeticiones from './../../js/utils/carrilPeticiones.js';
import preloaderDeLaSesion from './../../js/utils/preloaderDeLaSesion.js';
import cerrojoDeLaSesion from './../../js/utils/cerrojoDeLaSesion.js';

// Cuándo se vuelve a preguntar por el historial después de que la pasarela corte la conexión.
// El servidor sigue analizando el PDF y el apunte de la lectura no está escrito todavía, así que
// un único GET inmediato llega antes de tiempo: el historial se queda igual y el usuario concluye
// que tiene que volver a subir el documento, que es justo el trabajo duplicado que se evita.
const ESPERAS_TRAS_TIMEOUT = [5000, 15000, 30000, 60000];

export default {
  data: function() {
    return {
      municipios: [],
      lecturas: [],
      // 'cargando' | 'error' | 'listo'. Las dos listas de esta pantalla dicen que están vacías, y
      // eso solo se puede decir con la respuesta delante: mientras no llega hay que decir que está
      // cargando, y si falló, que falló (#262, la misma regla que la tabla de Ajustes · Festivos).
      // Arrancan en 'cargando' porque `onEntrar` pide las dos al entrar.
      estadoMunicipios: 'cargando',
      estadoLecturas: 'cargando',
      // Los ids que tenía el historial cuando salió el POST que está en vuelo (ver `leer`).
      baseDelEnvio: null,
      resultado: null,
      error403: false,
      refrescando: false,
      peticionResultado: 0,
      // Una recuperación por envío cortado, no una sola: dos boletines seguidos pueden acabar los
      // dos en timeout y cada uno espera a SU lectura (ver `programarReintentos`).
      recuperacionesPendientes: [],
      recuperacionesEnCurso: [],
      activa: true,
      enviando: false,
      envioEnCurso: 0,
      sesion: null,
      generacion: 0,
      dialogoAbierto: false,
      anio: new Date().getFullYear() + 1,
      fuenteRef: '',
      fuenteUrl: '',
      fichero: null,
      nombreFichero: ''
    };
  },
  computed: {
    // `enviando` apaga el botón mientras el POST está en vuelo: leer un boletín tarda, el fichero
    // sigue puesto todo ese rato y un doble clic mandaba el mismo PDF dos veces, con su análisis
    // y su apunte en el historial por duplicado.
    puedeLeer: function() {
      return !this.enviando && !!this.fichero && !!this.anio && !!(this.fuenteRef || '').trim();
    },
    /**
     * Por qué «Leer el boletín» está apagado, dicho junto al botón: antes se quedaba gris sin
     * decir qué le falta, y los tres campos que lo desbloquean están en el mismo diálogo pero
     * ninguno dice que haga falta. Misma regla que la tarjeta del municipio (§9 del CLAUDE.md del
     * orquestador): un control que no se puede usar dice por qué, quién lo desbloquea y dónde.
     *
     * Devuelve `null` cuando sí se puede pulsar: ahí no hay nada que explicar.
     */
    motivoNoPuedeLeer: function() {
      if (this.puedeLeer) return null;
      if (this.enviando) return this.$t('festivosboletin.leyendo');
      const falta = [];
      if (!this.anio) falta.push(this.$t('festivosboletin.faltaAnio'));
      if (!(this.fuenteRef || '').trim()) falta.push(this.$t('festivosboletin.faltaFuenteRef'));
      if (!this.fichero) falta.push(this.$t('festivosboletin.faltaPdf'));
      return this.$t('festivosboletin.faltaParaLeer', { falta: falta.join(', ') });
    }
  },
  created: function() {
    // Un carril por lista, y las dos listas de la pantalla van por el suyo: quién contesta y
    // cuándo se puede declarar un fallo lo decide el carril, no el `then` y el `catch` de cada
    // carga (ver carrilPeticiones). Hace falta porque la ruta es keepAlive y `onEntrar` pide las
    // dos listas cada vez que se entra: salir y volver antes de que conteste el primer GET deja
    // dos peticiones en vuelo de la misma sesión, y un número por cuenta no las distingue. Fuera
    // de `data` porque no es estado que se pinte.
    //
    // De quién era cada petición lo lleva el MISMO carril, por el sello de sesión que le pone
    // `comprobarSesion`: es la decisión de dirección del 06/10/2026 en la #262, después de que
    // cuatro rondas de revisión salieran todas en la juntura entre los dos vigilantes que había
    // antes —el carril y una guarda de sesión aparte—. Una sola decisión, un solo sitio.
    //
    // El de los municipios es el normal: cada entrada en la pantalla pide la lista otra vez y lo
    // que vale es la última petición lanzada.
    this.carrilMunicipios = carrilPeticiones();
    // El del historial es ACUMULATIVO: sus peticiones se solapan a propósito —el sondeo inmediato
    // tras un corte, los cuatro temporizados y el botón de actualizar— y todas piden exactamente
    // lo mismo, así que la que falla no puede invalidar a las que siguen en vuelo. Lo que manda es
    // la última respuesta APLICADA, y el fallo no se declara hasta que han fallado todas las que
    // podían contestar (revisión CODEX del PR #1759, rondas 1 y 3) —y «las que podían contestar»
    // son las de ESTA sesión, que es lo que cerró la ronda 4—.
    this.carrilLecturas = carrilPeticiones({ acumulativo: true });
  },
  // Los reintentos del historial siguen vivos aunque se salga de la pantalla: sin esto acaban
  // pidiendo lecturas para un componente que ya no existe.
  beforeDestroy: function() {
    this.activa = false;
    this.olvidarReintentos();
  },
  methods: {
    // La ruta se declara con keepAlive (routes.js), así que salir de la pantalla NO la destruye y
    // beforeDestroy no llega a ejecutarse: los reintentos seguirían un minuto pidiendo el
    // historial y sus avisos saldrían encima de la pantalla en la que esté el usuario.
    //
    // Y ese keepAlive es el del router de Framework7, que conserva la página en el DOM sin
    // envolver el componente en el <keep-alive> de Vue: los hooks activated/deactivated no se
    // disparan nunca aquí. Quien sí avisa de la entrada y la salida es la propia página.
    onEntrar: function() {
      this.activa = true;
      this.comprobarSesion();
      this.cargarMunicipios();
      // Si el timeout del POST llegó con el usuario ya fuera, la recuperación quedó guardada sin
      // sondear ni avisar: se retoma ahora. Su primer GET hace también la carga del historial,
      // así que no se pide dos veces —y vale para todas las que hubiera esperando, no una por una—.
      if (this.recuperacionesPendientes.length) this.reanudarRecuperaciones();
      else this.cargarLecturas();
    },
    onSalir: function() {
      var self = this;
      self.activa = false;
      // Salir cancela los sondeos, pero las lecturas que estaban esperando siguen vivas en el
      // servidor: se guardan para retomarlas al volver, igual que la que se corta con el usuario ya
      // fuera. Sin esto se perdía el resto del minuto de sondeos y el historial se quedaba sin
      // ellas. El aviso de la conexión cortada ya se dio en su momento, así que no se repite.
      self.recuperacionesEnCurso.forEach(function(enCurso) {
        if (!self.haLlegadoLaLectura(enCurso.conocidas, enCurso.esperada)) {
          self.recuperacionesPendientes.push({ esperada: enCurso.esperada, conocidas: enCurso.conocidas, mensaje: null });
        }
      });
      self.olvidarReintentos();
    },
    // Con quién se cargó lo que hay en pantalla. El usuario distingue dos cuentas y el token
    // cambia en cada login, así que juntos reconocen también que la misma persona ha vuelto a
    // entrar después de cerrar sesión.
    sesionActual: function() {
      var usuario = Utils.getUser();
      return ((usuario && usuario.username) || '') + '|' + Utils.getToken();
    },
    // Cerrar sesión NO destruye esta pantalla —la ruta es keepAlive—, así que quien entre después
    // en el mismo dispositivo se encontraría el detalle de la lectura que dejó abierto el anterior,
    // o su pantalla de «no autorizado», hasta abrir otra lectura o recargar la aplicación. Al
    // cambiar de sesión se tira todo lo que era de aquella cuenta, formulario incluido, y se
    // invalidan las respuestas que quedaran en vuelo: con el componente vivo llegarían a escribir
    // los municipios, el historial o el 403 del usuario anterior encima de la pantalla del nuevo.
    comprobarSesion: function() {
      var sesion = this.sesionActual();
      if (sesion === this.sesion) return;
      this.sesion = sesion;
      this.generacion += 1;
      // El sello de la sesión se pone en los dos carriles, que es lo que invalida de golpe lo que
      // tengan en vuelo: desde aquí ninguna de esas peticiones puede aplicar su lista, declarar un
      // fallo —ni su 403— ni contar como «todavía cargando» (ver carrilPeticiones). Se les pasa la
      // generación, un contador, y no `sesion`: el sello no tiene por qué llevar dentro el usuario
      // ni el token, al carril solo le hace falta saber que ha cambiado.
      this.carrilMunicipios.sesion(this.generacion);
      this.carrilLecturas.sesion(this.generacion);
      this.olvidarReintentos();
      // Las recuperaciones guardadas eran de la cuenta anterior: ni sus avisos ni sus sondeos
      // tienen sentido para quien entra ahora, que ni siquiera subió esos PDF.
      this.recuperacionesPendientes = [];
      this.municipios = [];
      this.lecturas = [];
      // Las dos listas se han vaciado: de la cuenta que entra no se sabe nada todavía, así que
      // ninguna de las dos puede afirmar que está vacía hasta que contesten sus GET (#262).
      this.estadoMunicipios = 'cargando';
      this.estadoLecturas = 'cargando';
      this.baseDelEnvio = null;
      this.resultado = null;
      this.error403 = false;
      this.refrescando = false;
      // El POST de la cuenta anterior puede seguir en vuelo; quien entra ahora no tiene por qué
      // esperarlo con el botón apagado, y al terminar ya no le desbloquea nada (ver `leer`).
      this.enviando = false;
      this.envioEnCurso = 0;
      this.dialogoAbierto = false;
      this.anio = new Date().getFullYear() + 1;
      this.fuenteRef = '';
      this.fuenteUrl = '';
      this.olvidarFichero();
    },
    // Las peticiones no se pueden cancelar, así que cada una recuerda con qué sesión salió y se
    // descarta al volver si ya no es la de ahora. Esto es para las que NO van por carril —el POST
    // del boletín y la lectura del detalle, que no pueden ir (ver `esElResultadoVigente`)—: las
    // dos listas no lo necesitan, porque su carril ya lleva el sello de la sesión.
    esDeLaSesion: function(generacion) {
      return generacion === this.generacion;
    },
    tiene: function(lista) {
      return !!(lista && lista.length);
    },
    fecha: function(valor) {
      if (!valor) return '—';
      // El back manda la fecha como "aaaa-mm-dd"; se pinta sin pasar por Date para que no
      // se corra un día por zona horaria.
      var partes = String(valor).substring(0, 10).split('-');
      return partes.length === 3 ? partes[2] + '/' + partes[1] + '/' + partes[0] : String(valor);
    },
    fechaHora: function(valor) {
      if (!valor) return '';
      return String(valor).replace('T', ' ').substring(0, 16);
    },
    // El fragmento del boletín del que sale el festivo: es lo que permite comprobar una propuesta
    // sin abrir el PDF. Los festivos dados de alta a mano no lo tienen.
    textoBoletin: function(festivo) {
      if (!festivo.textoBoletin) return '';
      return this.$t('festivosboletin.segunBoletin', { texto: festivo.textoBoletin });
    },
    etiquetaAmbito: function(festivo) {
      if (festivo.ambito === 'LOCAL') {
        return this.$t('festivosboletin.ambitoLocal') + ' · ' + (festivo.municipioNombre || festivo.municipioIne);
      }
      if (festivo.ambito === 'AUTONOMICO') {
        // Con plantilla en varias comunidades, «Autonómico» a secas no dice de cuál habla.
        return festivo.codComunidad
          ? this.$t('festivosboletin.ambitoAutonomicoDe', { comunidad: festivo.codComunidad })
          : this.$t('festivosboletin.ambitoAutonomico');
      }
      return this.$t('festivosboletin.ambitoNacional');
    },
    // Un aviso de discrepancia local se identifica por su municipio; uno autonómico o nacional no
    // tiene municipio, y sin esto la fila salía con el título en blanco.
    tituloDiscrepancia: function(discrepancia) {
      return discrepancia.municipioNombre || discrepancia.municipioIne
        || this.etiquetaAmbito(discrepancia);
    },
    crudo: function(propuesta) {
      try { return JSON.stringify(propuesta); } catch (e) { return ''; }
    },
    es403: function(e) {
      return !!(e && (e.status == 403 || (e.xhr && e.xhr.status == 403)));
    },
    // Con la ruta cacheada la pantalla sigue viva después de salir de ella, así que una respuesta
    // que llega tarde no puede soltar su alerta —que habla de Festivos— encima de otra pantalla.
    avisar: function(mensaje) {
      if (this.activa) {
        this.$f7.dialog.alert(mensaje);
      }
    },
    tratarError: function(e) {
      if (e && e.sesionCaducada) {
        // El adaptador ya ha cerrado la sesión y ha mandado al login. Aquí solo se recoge el
        // diálogo: no hay alerta que dar, porque la pantalla que queda debajo ya no es esta.
        this.dialogoAbierto = false;
        return;
      }
      if (e && e.timeout) {
        // La lectura puede haber terminado en el servidor aunque la conexión se cortara: se
        // cierra el diálogo y se refresca el historial para que se vea ahí antes de que a nadie
        // le dé por volver a subir el mismo PDF.
        this.dialogoAbierto = false;
        var esperada = this.lecturaEnCurso();
        // La base con la que se reconoce la lectura es la foto que se pidió al salir el POST
        // (`leer`), no la que haya ahora en pantalla: esa puede ser de hace rato y dejar fuera
        // lecturas que ya existían antes de este envío. Si no llegó a haberla, `conocidas` es
        // null y no se da por llegada ninguna lectura (ver haLlegadoLaLectura).
        var conocidas = this.baseDelEnvio;
        this.olvidarFichero();
        if (this.activa) {
          this.refrescarHistorialUnRato(conocidas, esperada);
          this.avisar(e.message);
        } else {
          // El timeout puede llegar DESPUÉS de onSalir: fuera de la pantalla no se sondea ni se
          // avisa, pero la lectura sigue viva en el servidor. Se guarda para retomarla al volver;
          // sin esto el usuario no se entera de nada y vuelve a subir el mismo PDF.
          this.recuperacionesPendientes.push({ esperada: esperada, conocidas: conocidas, mensaje: e.message });
        }
        return;
      }
      if (this.es403(e)) {
        // El popup vive fuera del v-else, así que si no se cierra a mano se queda encima del
        // mensaje de no autorizado dejando reintentar la lectura una y otra vez.
        this.dialogoAbierto = false;
        this.error403 = true;
      } else {
        this.avisar(e && e.message ? e.message : String(e || ''));
      }
    },
    // Por su carril, igual que las cargas de Ajustes · Festivos: solo escribe la última petición
    // lanzada. Con la lista sola no se notaba —la vieja pisaba la nueva y punto—, pero en cuanto el
    // estado de carga depende de la respuesta, el `catch` de una petición vieja llegando después de
    // que la nueva haya ido bien dejaba la pantalla en 'error', con su «no se ha podido cargar» y su
    // reintento, teniendo una respuesta buena y reciente aplicada; y su `tratarError` sacaba la
    // alerta —o ponía el 403 del usuario— por un fallo que ya no hablaba de lo que hay delante
    // (revisión CODEX del PR #1759). El historial va por el suyo, acumulativo (`cargarLecturas`).
    //
    // Y de quién era la petición lo lleva el propio carril: `comprobarSesion` le pone el sello, así
    // que una respuesta de la cuenta anterior ya no llega aquí —ni a `limpiar` ni a `fallo`—.
    cargarMunicipios: function() {
      var self = this;
      self.estadoMunicipios = 'cargando';
      self.carrilMunicipios.lanzar(RhFestivo.municipiosPlantilla(), {
        exito: function(lista) {
          self.municipios = lista;
          self.estadoMunicipios = 'listo';
        },
        // Y al fallar se vacía la lista, no solo se cambia el estado: unos municipios cargados en
        // una entrada anterior ya no respaldan nada —el GET de ahora no ha contestado— y dejarlos
        // puestos es enseñar datos viejos como si fueran la respuesta de esta vez. Es el criterio
        // que ya sigue la tabla de Ajustes · Festivos, que vacía al fallar (`cargar`, PR #1709),
        // y lo que señaló la revisión CODEX del PR #1759.
        limpiar: function() {
          self.municipios = [];
          self.estadoMunicipios = 'error';
        },
        fallo: function(e) {
          self.tratarError(e);
        }
      });
    },
    // `silencioso` es para los reintentos de después de un timeout: si la conexión sigue caída,
    // cada uno de ellos fallaría y sacaría su propio diálogo —hasta cinco encima del aviso que ya
    // se le dio al usuario—. El fallo no se esconde: el botón de actualizar el historial sigue
    // avisando, y ahí sí lo ha pedido alguien.
    //
    // Los sondeos se solapan —el inmediato, los cuatro temporizados y el botón de actualizar—, y
    // con la conexión lenta que provocó el timeout pueden resolverse en otro orden. Como cada uno
    // pisa `lecturas`, el que salió antes de registrarse la lectura volvería a dejar el historial
    // sin ella, y los temporizadores que quedan ya no lo arreglarían: al comprobar la lista buena
    // se dan por satisfechos y se saltan su GET.
    //
    // De ahí el carril acumulativo (ver `created`): todos piden lo mismo, así que lo que decide
    // qué se escribe es la última RESPUESTA aplicada y no la última petición lanzada. Con esto
    // último, un sondeo posterior que falla —en la misma conexión mala que provocó el timeout—
    // tiraba la respuesta buena que venía en camino, y si era la del último de los 60 segundos el
    // historial se quedaba obsoleto para siempre habiendo llegado la lectura.
    //
    // Resuelve con el historial que ESTA petición dejó en pantalla, o con null si no llegó a
    // aplicarse —falló, o ganó una respuesta posterior—. Es lo que permite a `leer` quedarse con
    // una base de verdad y no con lo que hubiera pintado de antes.
    cargarLecturas: function(silencioso) {
      var self = this;
      var aplicada = null;
      // #663 · Cerrojos y esperas de la sesión: si la sesión cambia en vuelo, la frontera calla la
      // continuación que los soltaba.
      cerrojoDeLaSesion(self, 'refrescando');
      // El estado sigue a la RESPUESTA aplicada, igual que `lecturas`: mientras no se haya aplicado
      // ninguna no se sabe nada y toca decir «cargando», pero en cuanto hay una, lo que está en
      // pantalla es real y un refresco posterior —o su fallo— no puede volver a ponerlo en duda.
      //
      // La pregunta es por el propio estado y no por el carril, cuya cuenta de aplicadas no se
      // reinicia al cambiar de sello: quien entra después se encuentra la lista vaciada por
      // `comprobarSesion`, y mirando ahí el estado se quedaría en 'listo' y la pantalla le diría
      // que no se ha leído ningún boletín sin haberlo preguntado por él.
      if (self.estadoLecturas !== 'listo') {
        self.estadoLecturas = 'cargando';
      }
      return self.carrilLecturas.lanzar(RhFestivo.lecturas(), {
        exito: function(lista) {
          self.lecturas = lista;
          self.estadoLecturas = 'listo';
          aplicada = lista;
        },
        // El carril solo llama aquí cuando han fallado TODAS las peticiones de esta sesión que
        // podían contestar: mientras quede alguna en vuelo, el historial sigue en camino y decir
        // que no se ha podido cargar —con su alerta— sería desmentir una respuesta que puede
        // llegar bien (revisión CODEX del PR #1759, ronda 3). Y «de esta sesión»: las de la cuenta
        // anterior no son respuestas en camino para quien está delante, así que no pueden callar
        // el fallo de la suya (ronda 4).
        //
        // Y solo de 'cargando' a 'error': una respuesta buena ya aplicada no se pone en duda
        // porque un sondeo posterior falle, igual que no se le pisa la lista.
        limpiar: function() {
          if (self.estadoLecturas === 'cargando') {
            self.estadoLecturas = 'error';
          }
        },
        // `silencioso` es de la petición que acaba declarando el fallo, que es la que habla de lo
        // que hay delante. Si la que lo declara era un sondeo, no se saca alerta: el aviso de un
        // sondeo nunca lo pidió nadie, y el mensaje de la lista ya lo dice igual.
        fallo: function(e) {
          if (!silencioso) self.tratarError(e);
        }
      }).then(function() {
        // El botón de actualizar se reabre cuando no queda ninguna petición en vuelo, no cuando
        // contesta la última lanzada: con dos solapadas, la primera que vuelve no es el final del
        // refresco. Y el carril no cuenta las de la sesión anterior: si no, el botón de quien entra
        // después se queda apagado esperando un GET que ya no le va a decir nada —para siempre si
        // no termina nunca— (revisión CODEX del PR #1759, ronda 4).
        if (!self.carrilLecturas.enVuelo()) self.refrescando = false;
        return aplicada;
      });
    },
    // Se pregunta ya y se vuelve a preguntar un rato, hasta que la lectura aparezca: el servidor
    // todavía la está registrando cuando la conexión se corta. Si ni así aparece, queda el botón
    // de actualizar el historial, que es lo que evita dar la lectura por perdida y repetirla.
    //
    // Fuera de la pantalla no se sondea —el keepAlive la mantiene viva pero nadie la está
    // mirando—: quien recibe el timeout estando fuera no llama aquí, guarda la recuperación y la
    // retoma al volver con reanudarRecuperacion.
    refrescarHistorialUnRato: function(conocidas, esperada) {
      var self = this;
      self.cargarLecturas(true);
      self.programarReintentos(conocidas, esperada);
    },
    //
    // Cada envío cortado tiene su propia recuperación, con sus temporizadores y lo que espera:
    // arrancar la del segundo boletín cancelando la del primero perdía esa primera lectura. Los
    // sondeos que quedaban eran ya los del segundo, y estos se apagan en cuanto llega LA SUYA, así
    // que una primera lectura que terminara después no volvía a aparecer sola y el historial se
    // quedaba sin ella —justo lo que lleva a subir otra vez el mismo PDF—. Que los sondeos de dos
    // recuperaciones se solapen no estorba: cada GET lleva su número (ver `cargarLecturas`).
    programarReintentos: function(conocidas, esperada) {
      var self = this;
      // Qué está esperando esta recuperación, por si el usuario se va antes de que termine:
      // onSalir las cancela todas y necesita saber qué dejar guardado para el regreso.
      var recuperacion = { conocidas: conocidas, esperada: esperada, reintentos: [] };
      self.recuperacionesEnCurso.push(recuperacion);
      ESPERAS_TRAS_TIMEOUT.forEach(function(espera, indice) {
        recuperacion.reintentos.push(setTimeout(function() {
          if (self.haLlegadoLaLectura(conocidas, esperada)) {
            self.olvidarRecuperacion(recuperacion);
            return;
          }
          self.cargarLecturas(true);
          // Agotado el último sondeo ya no hay nada que retomar al volver: a partir de aquí lo
          // que queda es el botón de actualizar el historial.
          if (indice === ESPERAS_TRAS_TIMEOUT.length - 1) self.olvidarRecuperacion(recuperacion);
        }, espera));
      });
    },
    // Da por terminada UNA recuperación: apaga lo que le quedara por sondear y la saca de la lista,
    // sin tocar las de los demás envíos.
    olvidarRecuperacion: function(recuperacion) {
      recuperacion.reintentos.forEach(clearTimeout);
      recuperacion.reintentos = [];
      var indice = this.recuperacionesEnCurso.indexOf(recuperacion);
      if (indice !== -1) this.recuperacionesEnCurso.splice(indice, 1);
    },
    // Las lecturas que se cortaron mientras el usuario estaba fuera se retoman al volver. Primero
    // se mira el historial —de una vez para todas, que la lista es la misma—: las que entretanto
    // quedaran registradas no tienen nada que contar —están ahí— y sacar entonces el aviso de la
    // conexión cortada solo asustaría. Las que todavía no estén se vuelven a sondear, cada una a
    // lo suyo, y se da el aviso que en su momento se calló.
    reanudarRecuperaciones: function() {
      var self = this;
      var pendientes = self.recuperacionesPendientes;
      var generacion = self.generacion;
      self.recuperacionesPendientes = [];
      self.cargarLecturas(true).then(function() {
        if (!self.esDeLaSesion(generacion)) return;
        var vivas = pendientes.filter(function(pendiente) {
          return !self.haLlegadoLaLectura(pendiente.conocidas, pendiente.esperada);
        });
        if (!vivas.length) return;
        // Si se ha vuelto a salir antes de que llegara el historial, se dejan guardadas otra vez:
        // el aviso y los sondeos siguen siendo para cuando esta pantalla esté delante. Delante van
        // las de antes, que llevan más tiempo esperando.
        if (!self.activa) {
          self.recuperacionesPendientes = vivas.concat(self.recuperacionesPendientes);
          return;
        }
        vivas.forEach(function(pendiente) {
          self.programarReintentos(pendiente.conocidas, pendiente.esperada);
        });
        // Sin mensaje es que la recuperación venía de salirse a mitad de los sondeos: el aviso de
        // la conexión cortada ya se dio entonces y repetirlo al volver solo confunde. Y con dos
        // lecturas cortadas el aviso es el mismo, así que se da una sola vez en vez de encadenar
        // dos diálogos idénticos.
        var conAviso = vivas.filter(function(pendiente) { return !!pendiente.mensaje; })[0];
        if (conAviso) self.avisar(conAviso.mensaje);
      });
    },
    // Con qué datos se reconoce en el historial la lectura que se acaba de cortar. Se recogen
    // ANTES de olvidar el fichero, que es lo que borra su nombre del formulario. `fuenteRef` va
    // recortado porque el servidor lo guarda así, y el año en texto porque el formulario lo da
    // así y el historial lo devuelve como número.
    lecturaEnCurso: function() {
      var usuario = Utils.getUser() && Utils.getUser().username;
      return {
        usuario: usuario || '',
        anio: this.anioComparable(this.anio),
        fuenteRef: (this.fuenteRef || '').trim(),
        nombreFichero: this.nombreFichero || ''
      };
    },
    // El año viaja como número desde el servidor y como texto desde el <input>, así que se
    // compara normalizado; si no es un año legible se devuelve vacío y el sondeo sigue hasta
    // el final en vez de darse por satisfecho con una lectura que no se sabe si es la suya.
    anioComparable: function(valor) {
      var numero = parseInt(valor, 10);
      return isNaN(numero) ? '' : String(numero);
    },
    // El historial es de todos, y una misma cuenta puede tener dos lecturas en marcha a la vez
    // —otra pestaña, otro dispositivo—, así que ni «han aparecido lecturas nuevas» ni «hay una
    // mía nueva» valen como señal: la primera ajena cortaría los sondeos antes de que llegue la
    // que se está esperando. Lo que se busca es ESTA lectura: el documento y el boletín que se
    // estaban subiendo cuando se cortó la conexión, registrados por mí. El año entra en la
    // comparación porque el mismo PDF y la misma referencia se leen un año y otro, y sin él la
    // lectura de 2025 daría por llegada la de 2026. Si falta con qué compararla se sondea el
    // minuto entero, que son cuatro GET.
    haLlegadoLaLectura: function(conocidas, esperada) {
      var self = this;
      // Sin base de partida (ver `leer`) no hay forma de distinguir la lectura nueva de una
      // anterior igual, así que no se da por llegada ninguna: se sondea el minuto entero, que
      // son cuatro GET, y queda el botón de actualizar el historial.
      if (!conocidas) return false;
      if (!esperada || !esperada.usuario || !esperada.nombreFichero || !esperada.anio) return false;
      return this.lecturas.some(function(l) {
        return conocidas.indexOf(l.idLectura) === -1
          && l.usuario === esperada.usuario
          && self.anioComparable(l.anio) === esperada.anio
          && (l.nombreFichero || '') === esperada.nombreFichero
          && (l.fuenteRef || '') === esperada.fuenteRef;
      });
    },
    // Apaga los sondeos de TODAS las recuperaciones: se sale de la pantalla, se cambia de sesión o
    // se destruye el componente. Lo que haya que retomar después lo guarda quien llama.
    olvidarReintentos: function() {
      this.recuperacionesEnCurso.forEach(function(recuperacion) {
        recuperacion.reintentos.forEach(clearTimeout);
        recuperacion.reintentos = [];
      });
      this.recuperacionesEnCurso = [];
    },
    abrirDialogo: function() {
      this.dialogoAbierto = true;
    },
    onFicheroSeleccionado: function(evento) {
      var fichero = evento.target.files && evento.target.files[0];
      this.fichero = fichero || null;
      this.nombreFichero = fichero ? fichero.name : '';
    },
    // El popup no se destruye al cerrarse, así que el <input type="file"> conserva lo elegido.
    // Sin vaciarlo, volver a elegir el MISMO PDF no dispara "change" y el botón se queda apagado.
    olvidarFichero: function() {
      this.fichero = null;
      this.nombreFichero = '';
      if (this.$refs.inputPdf) {
        this.$refs.inputPdf.value = '';
      }
    },
    // Quién manda sobre lo que se pinta en «Resultado de la lectura». Lo piden dos sitios —la
    // subida y cada lectura del historial—, y las respuestas no vuelven en el orden en que se
    // pidieron: pulsar dos lecturas seguidas dejaba en pantalla la primera si llegaba la última,
    // y una consulta al historial podía tapar el resultado de una subida ya terminada. Solo
    // escribe la petición más reciente; como los carriles, el contador no se reinicia.
    //
    // Esto NO va por `carrilPeticiones` aunque su política sea la misma, y es a propósito: una de
    // las dos peticiones es una ESCRITURA (el POST del boletín), y su fallo y su 403 tienen que
    // llegar al usuario aunque entretanto haya abierto otra lectura —ha subido un PDF y no se ha
    // leído—. El carril se los comería como «fuera de turno». Lo que sí es carrera de pintado
    // —qué resultado queda delante— se mira aquí, y se mira en el `then` y en el `catch` de los
    // dos llamantes, que es lo que faltaba en la lista de municipios.

    esElResultadoVigente: function(peticion) {
      return peticion === this.peticionResultado;
    },
    leer: function() {
      var self = this;
      var app = self.$f7;
      var generacion = self.generacion;
      // El botón ya se apaga con `enviando`, pero un doble toque puede colarse antes de que Vue
      // repinte, así que la guarda de verdad está aquí.
      if (self.enviando) return;
      var peticion = self.peticionResultado + 1;
      self.peticionResultado = peticion;
      cerrojoDeLaSesion(self, 'enviando');
      self.envioEnCurso = peticion;
      // Contra qué historial se reconocerá esta lectura si el POST se corta. Se pide AHORA, al
      // salir el envío, y no cuando llega el timeout: lo que hay en pantalla puede ser la carga
      // de al entrar, y entretanto otra pestaña u otro dispositivo pueden haber completado una
      // lectura mía del mismo año, fichero y referencia. Esa lectura, anterior a este envío, no
      // estaría en la foto vieja, y el primer sondeo la tomaría por la de ahora y cortaría la
      // recuperación con el POST todavía en marcha.
      //
      // Va en paralelo al POST a propósito: esperarla dejaría la lectura sin arrancar si la
      // conexión que va a provocar el timeout tampoco deja pasar este GET. Si no llega a tiempo
      // o falla, `baseDelEnvio` se queda en null y no se da por llegada ninguna lectura.
      self.baseDelEnvio = null;
      self.cargarLecturas(true).then(function(lista) {
        if (lista && self.esDeLaSesion(generacion) && self.enviando && self.envioEnCurso === peticion) {
          self.baseDelEnvio = lista.map(function(l) { return l.idLectura; });
        }
      });
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      RhFestivo.leerBoletin(self.fichero, self.anio, self.fuenteRef, self.fuenteUrl)
        .then(function(resultado) {
          if (!self.esDeLaSesion(generacion)) return;
          if (self.esElResultadoVigente(peticion)) self.resultado = resultado;
          self.dialogoAbierto = false;
          self.olvidarFichero();
          self.cargarLecturas();
        })
        // Sin la condición de `esElResultadoVigente`, y a diferencia de `verLectura`: este fallo no
        // habla de lo que hay pintado, habla del PDF que acaba de subir quien está delante. Que
        // entretanto haya abierto otra lectura del historial no lo convierte en un error viejo:
        // su boletín no se ha leído y tiene que saberlo.
        .catch(function(e) { if (self.esDeLaSesion(generacion)) self.tratarError(e); })
        .then(function() {
          espera.cerrar();
          // Solo se desbloquea el envío que estaba en curso: si entretanto cambió la sesión o
          // arrancó otro, este de aquí no puede reabrirle el botón a un POST que sigue vivo.
          if (self.envioEnCurso === peticion) self.enviando = false;
        });
    },
    verLectura: function(idLectura) {
      var self = this;
      var app = self.$f7;
      var generacion = self.generacion;
      var peticion = self.peticionResultado + 1;
      self.peticionResultado = peticion;
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      RhFestivo.lectura(idLectura)
        .then(function(detalle) {
          // El detalle guarda el resultado tal y como se escribió en rh_audit aquel día.
          if (self.esDeLaSesion(generacion) && self.esElResultadoVigente(peticion)) {
            self.resultado = Object.assign({}, detalle, detalle && detalle.resultado);
          }
        })
        // El fallo se trata con la misma condición que el acierto: si mientras venía se pulsó otra
        // lectura, este error ya no habla de lo que hay en pantalla. Dejarlo pasar sacaba una alerta
        // sobre la lectura buena ya pintada y, con un 403 de la vieja, cambiaba toda la pantalla por
        // «no autorizado» encima de un detalle que se había cargado bien.
        .catch(function(e) {
          if (self.esDeLaSesion(generacion) && self.esElResultadoVigente(peticion)) self.tratarError(e);
        })
        .then(function() { espera.cerrar(); });
    }
  }
};
</script>
